<template>
  <div v-if="quantity > 0">
    <LineItemAddon v-if="isAddOn" v-bind="{ lineItem }" />

    <div v-else class="flex items-center pr-4 bg-white shadow-md">
      <div class="w-full max-w-[128px]">
        <LinkField v-if="href && variantAvailable" :to="href" @click.native="close" class="block" no-tracking>
          <FixedAspect :ratio="2 / 3">
            <CldnImg
              v-if="variant.image"
              :src="variant.image.src"
              :alt="variant.image.altText"
              :quality="90"
              crop="fit"
              sizes="35vw sm:20vw md:10vw lg:30vw xl:10vw 2xl:10vw"
            />
            <div
              v-else
              class="flex items-center justify-center w-full h-full bg-gray-200 text-gray-800 text-xs text-center p-1"
            >
              {{ $t('gallery.no-images') }}
            </div>
          </FixedAspect>
        </LinkField>
      </div>
      <div class="w-full">
        <div class="gap-2 grid grid-cols-4 items-center py-2 w-full pl-2">
          <div class="fluid-sm leading-tightest col-span-3">
            <LinkField
              v-if="href && lineItem.variant?.product?.title"
              :to="href"
              class="inline-block font-medium"
              @click.native="close"
              no-tracking
            >
              {{ lineItem.variant?.product?.title }}
            </LinkField>
            <div v-else-if="!href && lineItem.variant?.product?.title" class="line-clamp-2 font-medium">
              {{ lineItem.variant?.product?.title }}
            </div>
            <div v-if="!isCourse && variantAvailable && variantValue" class="text-gray-light mt-0.5">
              {{ variantValue }}
            </div>
            <div v-else-if="!variantAvailable" class="text-gray-light font-medium mt-0.5">
              {{ $t('cart.variantNotAvailable') }}
            </div>
            <div
              v-if="hasUnitPrice"
              class="sm:hidden before:content-['('] after:content-[')'] whitespace-nowrap fluid-xs leading-tightest text-gray-light mt-[2px]"
            >
              {{ unitPriceLine }}
            </div>
          </div>
          <div class="text-right col-span-4 sm:col-span-1">
            <div class="fluid-xs leading-tightest font-medium">
              <span v-if="itemDiscountedPrice > 0">
                {{
                  $n(parseFloat(itemDiscountedPrice), {
                    style: 'currency',
                    currency: price.currencyCode,
                  })
                }}
              </span>
              <span v-else>{{ $t('common.forFree') }}</span>
            </div>
            <div v-if="itemDiscountValue || compareAtPrice" class="text-gray-light fluid-xs leading-tightest mt-[2px]">
              <s v-if="itemDiscountValue">
                {{
                  $n(parseFloat(itemDiscountValue), {
                    style: 'currency',
                    currency: price.currencyCode,
                  })
                }}
              </s>
              <s v-else-if="compareAtPrice" class="fluid-xs leading-tightest text-gray-light">
                {{
                  $n(parseFloat(compareAtPrice.amount), {
                    style: 'currency',
                    currency: compareAtPrice.currencyCode,
                  })
                }}
              </s>
            </div>
            <div
              v-if="hasUnitPrice && itemDiscountedPrice > 0"
              class="hidden sm:block before:content-['('] after:content-[')'] whitespace-nowrap fluid-xs leading-tightest text-gray-light mt-[2px]"
            >
              {{ unitPriceLine }}
            </div>
          </div>
          <div class="flex items-center justify-end sm:justify-end fluid-xs leading-tightest col-span-4 sm:col-span-4">
            <button
              data-testid="btn-decrement"
              class="border border-gray-300 border-r-0 flex items-center justify-center w-8 h-8 fluid-xs leading-tightest font-medium transition-all rounded-l shrink-0"
              :aria-label="$t('cart.decrement')"
              @click="decrement"
            >
              -
            </button>
            <input
              name="quantity"
              type="number"
              min="1"
              :value="quantity"
              class="border border-gray-300 w-10 px-1 h-8 text-center appearance-text"
              :aria-label="$t('cart.quantity')"
              @blur="updateQuantity"
              @keypress.enter="updateQuantity"
            />
            <button
              data-testid="btn-increment"
              class="border border-gray-300 border-l-0 flex items-center justify-center w-8 h-8 fluid-xs leading-tightest font-medium transition-all rounded-r shrink-0"
              :class="[{ 'pointer-events-none opacity-50': maxQuantity > 0 && quantity >= maxQuantity }]"
              :aria-label="$t('cart.increment')"
              @click="increment"
            >
              +
            </button>

            <button class="ml-2" @click="removeLineItem" :aria-label="$t('cart.removeLineItem')">
              <BinIcon class="h-4" />
            </button>
          </div>
        </div>
        <GenericFormError class="pl-2 -mt-2" v-if="hasQuantityError">
          {{ hasQuantityError }}
        </GenericFormError>
      </div>
    </div>
  </div>
</template>

<script>
import LinkField from '@/components/common/LinkField';
import FixedAspect from '@/components/common/FixedAspect';
import CldnImg from '@/components/CldnImg';
import LineItemAddon from '@/components/cart/LineItemAddon';
import GenericFormError from '@/components/account/GenericFormError';
import BinIcon from '@/components/icons/BinIcon';
import { useCartStore } from '@/stores/cart';
import { storeToRefs } from 'pinia';

export default defineNuxtComponent({
  components: { FixedAspect, LinkField, CldnImg, LineItemAddon, GenericFormError, BinIcon },

  props: {
    lineItem: {
      type: Object,
      default: null,
    },
    index: {
      type: Number,
      default: 0,
    },
  },

  setup() {
    const cartStore = useCartStore();
    const { errors, cart } = storeToRefs(cartStore);

    const close = () => cartStore.close();
    const changeQty = (payload) => cartStore.changeQty(payload);

    return {
      errors,
      cart,
      close,
      changeQty,
    };
  },

  data() {
    return {
      quantity: this.lineItem?.quantity || 1,
    };
  },

  computed: {
    hasQuantityError() {
      const error = this.errors?.find((error) => {
        return (
          error.field.includes('lines') &&
          error.field.includes(this.index.toString()) &&
          error.field.includes('quantity')
        );
      });

      if (!error) return false;

      return error.message;
    },

    isAddOn() {
      const IS_ADDON_KEY = '_isAddOn';

      return (
        this.lineItem?.attributes.findIndex((attr) => {
          return attr.key === IS_ADDON_KEY && attr.value === 'true';
        }) !== -1
      );
    },

    isCourse() {
      return (
        this.lineItem?.attributes.findIndex((attr) => {
          return attr.key === '_isCourse' && attr.value === 'true';
        }) !== -1
      );
    },

    variant() {
      return this.lineItem?.variant;
    },

    variantAvailable() {
      return !!this.variant;
    },

    variantValue() {
      return this.variant?.title;
    },

    price() {
      const price = this.variant.price;
      return {
        ...price,
        amount: price.amount * this.quantity,
      };
    },

    compareAtPrice() {
      const price = this.variant.compareAtPrice;
      if (!price) return null;
      return {
        ...price,
        amount: price.amount * this.quantity,
      };
    },

    hasUnitPrice() {
      return this.lineItem.variant.unitPrice !== null;
    },

    unitPriceLine() {
      if (!this.variant.unitPrice) return '';

      const price = this.$n(parseFloat(this.variant.unitPrice.amount), {
        style: 'currency',
        currency: this.variant.unitPrice.currencyCode,
      });

      return `${price} / ${this.variant.unitPriceMeasurement.referenceUnit.toLowerCase()}`;
    },

    href() {
      return this.$localeRootPath(
        this.variant?.product?.handle &&
          this.variant?.sku &&
          `/products/${this.variant.product.handle}?sku=${this.variant.sku}`
      );
    },

    itemDiscount() {
      return this.lineItem.discountAllocations
        .map((allocation) => +allocation.discountedAmount.amount)
        .reduce((a, b) => a + b, 0);
    },

    itemDiscountedPrice() {
      return this.price.amount - this.itemDiscount;
    },

    itemDiscountValue() {
      return this.itemDiscount ? this.price.amount : false;
    },

    maxQuantity() {
      const attribute = this.lineItem.attributes.find((attr) => {
        return attr.key === '_maxQuantity';
      });

      return attribute ? Number(attribute.value) : false;
    },
  },

  watch: {
    // since we pre update the quantity, before it is send to shopify api,
    // we need to update the line item quantity at some times here
    lineItem(newLineItem, oldValue) {
      if (newLineItem.quantity !== oldValue.quantity || !!this.hasQuantityError) {
        this.quantity = newLineItem.quantity;
      }
    },
  },

  methods: {
    updateQuantity(event) {
      const value = parseInt(event.target.value, 10);
      if (isNaN(value) || value < 1) {
        this.quantity = 1;
      } else if (this.maxQuantity && value > this.maxQuantity) {
        this.quantity = this.maxQuantity;
      } else {
        this.quantity = value;
      }
      this.changeQty({ id: this.lineItem.id, quantity: this.quantity });
    },

    removeLineItem() {
      this.changeQty({ id: this.lineItem.id, quantity: 0 });
    },

    increment() {
      this.changeQty({ id: this.lineItem.id, quantity: ++this.quantity });
    },

    decrement() {
      this.changeQty({ id: this.lineItem.id, quantity: --this.quantity });
    },
  },
});
</script>
